import './index.scss';
import {
  useCloseOldModal,
  useOpenOldModal,
} from '../../../../store/modal/modalHooks';
import { useEffect, useState } from 'react';
import BookLoungeForm from '../BookLoungeForm';
import Toast from '../../../shared/Toast/Toast';
import { useTranslation } from 'react-i18next';
import BookLoungeDetails from '../BookLoungeDetails';
import { Button, Form, Input, Label } from 'reactstrap';
import { Controller, UseFormReturn } from 'react-hook-form';
import {
  useFetchResourceAlloc,
  useCancelBookResource,
  useUpdateResourceAlloc,
  useResourceManagementSlots,
} from '../../../../store/RoomManagement/resourceManagementHooks';
import { CreditTypesEnum } from '../../../shared/BookingCheckout';
import { useMyCompanyType } from '../../../../store/Auth/authHooks';
import { stringHour } from '../../../../utility/functions/stringHour';
import BookingViewCheckout from '../../../shared/BookingViewCheckout';
import BookingEditCheckout from '../../../shared/BookingEditCheckout';
import { ModalComponentEnum } from '../../../../store/modal/modalTypes';
import { getTimeStamp } from '../../../../utility/functions/getTimeString';
import isCreditDisabled from '../../../../utility/services/is-credit-disabled';
import { LoungeResource } from '../../../../store/lounges/loungesManagementTypes';
import { MyActivityResourceImpl } from '../../../../API/my-activities/implements/my-activity-resource.impl';

type PropsTypes = {
  activityId: number;
  form: UseFormReturn<any, object>;
  lounge: LoungeResource;
  setShowSuccessPage: (value: boolean) => void;
  setShowCancellationPolicy: (value: boolean) => void;
  activity: MyActivityResourceImpl;
  showCancellationPolicy: boolean;
};

const ModifyLoungeFormContainer = ({ activity, ...props }: PropsTypes) => {
  const { t } = useTranslation();
  const openModal = useOpenOldModal();
  const closeModal = useCloseOldModal();
  const companyType = useMyCompanyType();
  const cancelResource = useCancelBookResource();
  const resourceSlots = useResourceManagementSlots();
  const fetchResourceAlloc = useFetchResourceAlloc();
  const updateResourceAlloc = useUpdateResourceAlloc();

  const [totalPrice, setTotalPrice] = useState(0);
  const [originalDate, setOriginalDate] = useState<any>(); // Store the original date here
  const [originalEndTime, setOriginalEndTime] = useState<any>(); // Store the original end time here
  const [originalStartTime, setOriginalStartTime] = useState<any>(); // Store the original start time here
  const [showBookingCheckout, setShowBookingCheckout] = useState(true);

  const { handleSubmit, control } = props.form;

  const onSubmit = async (data: any) => {
    const newData = {
      start_time: getTimeStamp(data.date[0], stringHour(data.start)),
      end_time: getTimeStamp(data.date[0], stringHour(data.end)),
      resource_id: props.lounge.id,
      number_of_participants: +data.numberOfParticipants,
      user_message: props.form.watch('messageForAdmin'),
      resource_alloc_id: props.activityId,
    };

    updateResourceAlloc(newData)
      .then(() => {
        Toast('success-request', t('BOOKING_UPDATED_SUCCESSFULLY'), t);
        props.setShowSuccessPage(true);
      })
      .catch((error) => {
        Toast(
          'error',
          error?.response?.data?.error?.message || 'Unavailable at this time',
          t
        );
      });
  };

  useEffect(() => {
    if (props.form.watch('date')?.length) {
      const timeStamp = new Date(props.form.watch('date')[0]).getTime();
      fetchResourceAlloc(
        props.lounge.id,
        timeStamp,
        props.activityId
      ).finally();
    }
  }, [props.form.watch('date')?.toString()]);

  useEffect(() => {
    if (!originalStartTime && props.form.watch('start')) {
      setOriginalStartTime(props.form.watch('start'));
    }

    if (!originalStartTime && props.form.watch('end')) {
      setOriginalEndTime(props.form.watch('end'));
    }

    if (!originalStartTime && props.form.watch('date')) {
      setOriginalDate(props.form.watch('date'));
    }
  }, [
    props.form.watch('start'),
    props.form.watch('end'),
    props.form.watch('date'),
  ]);

  useEffect(() => {
    const isOriginalConfiguration =
      new Date(props.form.watch('date')).setHours(0, 0, 0, 0) ===
        new Date(originalDate).setHours(0, 0, 0, 0) &&
      props.form.watch('start') === originalStartTime &&
      props.form.watch('end') === originalEndTime;

    setShowBookingCheckout(isOriginalConfiguration);
  }, [
    props.form.watch('end'),
    props.form.watch('start'),
    props.form.watch('date'),
  ]);

  useEffect(() => {
    let minimumPrice = props.lounge.bookingPrice;

    const specialPrice = props.lounge.specialPrices.find(
      (special) => special.companyType === companyType?.id
    );

    if (specialPrice && specialPrice.price < minimumPrice) {
      minimumPrice = specialPrice.price;
    }

    const sortedPricingRules = [...props.lounge.pricingRules].sort(
      (a, b) => b.minHours - a.minHours
    );

    const pricingRule = sortedPricingRules.find(
      (rule) =>
        rule.minHours <= props.form.watch('end') - props.form.watch('start')
    );

    if (pricingRule && pricingRule.bookingPrice < minimumPrice) {
      minimumPrice = pricingRule.bookingPrice;
    }

    setTotalPrice(
      (props.form.watch('end') - props.form.watch('start')) * minimumPrice
    );
  }, [
    props.form.watch('end'),
    props.form.watch('start'),
    props.lounge.bookingPrice,
    companyType,
  ]);

  const onShowPolicy = () => {
    if (props.showCancellationPolicy) {
      props.setShowCancellationPolicy(false);
    } else {
      props.setShowCancellationPolicy(true);
    }
  };

  const onCancelBooking = () => {
    openModal(ModalComponentEnum.QUESTION_CANCEL_ALLOC_MODAL, {
      data: { activity },
      callBack: () => {
        cancelResource(props.activityId, (isSuccessful, message) => {
          const toastType = isSuccessful ? 'success-request' : 'error';
          Toast(toastType, message, t);
        }).finally(() => {
          closeModal();
        });
      },
    });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="book-lounge-modal-form">
        <div className="book-lounge-modal-form-title">
          {t('LOUNGE_BOOKING_DETAILS')}
        </div>
        <BookLoungeDetails lounge={props.lounge} />
        <BookLoungeForm
          form={props.form}
          lounge={props.lounge}
          originalArray={resourceSlots}
        />
        <div className="message-form">
          <Label for="departmentName">{t('DEPARTMENT_NAME_LABEL')}</Label>
          <Controller
            name="departmentName"
            control={control}
            render={({ field }) => (
              <Input
                id="departmentName"
                type="text"
                className="text-input"
                placeholder={t('DEPARTMENT_NAME_LABEL')}
                {...field}
              />
            )}
          />
        </div>
        <div className="message-form">
          <Label for="meetingSubject">{t('MEETING_SUBJECT_LABEL')}</Label>
          <Controller
            name="meetingSubject"
            control={control}
            render={({ field }) => (
              <Input
                id="meetingSubject"
                type="text"
                className="text-input"
                placeholder={t('MEETING_SUBJECT_LABEL')}
                {...field}
              />
            )}
          />
        </div>
        <div className="message-form">
          <Label for="messageForAdmin">{t('MESSAGE_FOR_ADMIN_LABEL')}</Label>
          <Controller
            name="messageForAdmin"
            control={control}
            render={({ field }) => (
              <Input
                id="messageForAdmin"
                type="textarea"
                className="text-input"
                placeholder={t('MESSAGE_FOR_ADMIN_TEXT')}
                {...field}
              />
            )}
          />
        </div>
        {showBookingCheckout ? (
          <BookingViewCheckout
            creditTypes={CreditTypesEnum.COMPANY}
            price={totalPrice}
            discount={
              props.lounge.isPremium
                ? companyType?.premiumDiscount
                : companyType?.discount
            }
            disabledCreditUser={isCreditDisabled(
              props.lounge.enableCreditUse,
              props.form.watch('date')
            )}
            activityId={props.activityId}
          />
        ) : (
          <BookingEditCheckout
            creditTypes={CreditTypesEnum.COMPANY}
            price={totalPrice}
            discount={
              props.lounge.isPremium
                ? companyType?.premiumDiscount
                : companyType?.discount
            }
            disabledCreditUser={isCreditDisabled(
              props.lounge.enableCreditUse,
              props.form.watch('date')
            )}
            activityId={props.activityId}
          />
        )}
        <div className="book-lounge-form-actions">
          <Button
            className="confirm-booking-button"
            type="submit"
            color="primary"
          >
            {t('SAVE_CHANGES')}
          </Button>
          <Button
            className="cancel-booking-button"
            type="button"
            onClick={onCancelBooking}
            color="transparent"
            style={{ backgroundColor: '#ea5455', color: 'white' }}
          >
            {t('CANCEL_BOOKING')}
          </Button>
          <Button
            className="cancel-booking-button"
            type="button"
            color=""
            onClick={closeModal}
          >
            {t('CREATE_NEW_SERVICE_CALL_CLOSE_BUTTON')}
          </Button>{' '}
        </div>
        <div className="view-cancellation-policy-btn">
          <span onClick={() => onShowPolicy()}>
            {props.showCancellationPolicy
              ? t('BACK_BUTTON_TEXT')
              : t('VIEW_CANCELLATION_POLICY')}
          </span>
        </div>
      </div>
    </Form>
  );
};

export default ModifyLoungeFormContainer;
